import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Search, Users, ChevronLeft, ChevronRight, Link2, Unlink, Loader2, X,
} from 'lucide-react';

const PRIMARY = '#0099FF';

const ClientesAsociados = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { token, API_URL } = useAuth();

  const [clientes, setClientes] = useState([]);
  const [total, setTotal] = useState(0);
  const [comercialName, setComercialName] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Vincular state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [vinculando, setVinculando] = useState(null);
  const [desvinculando, setDesvinculando] = useState(null);
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      const res = await fetch(`${API_URL}/commercial/clientes-asociados/${userId}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error');
      const data = await res.json();
      setClientes(data.clientes || []);
      setTotal(data.total || 0);
      setComercialName(data.comercialName || '');
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, userId, page]);

  useEffect(() => { fetchClientes(); }, [fetchClientes]);

  // Click outside to close dropdown
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback(async (term) => {
    if (!term || term.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/clients/search-all?q=${encodeURIComponent(term)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Filter out clients already linked to this commercial
        const linked = new Set(clientes.map(c => c.id));
        setSearchResults((data.clients || []).filter(c => !linked.has(c.id)));
      }
    } catch { /* ignore */ }
    setSearching(false);
  }, [API_URL, token, clientes]);

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setShowDropdown(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleVincular = async (clientId) => {
    setVinculando(clientId);
    try {
      const res = await fetch(`${API_URL}/commercial/clientes-asociados/vincular`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, userId: parseInt(userId) }),
      });
      if (!res.ok) throw new Error('Error');
      setSearchQuery('');
      setSearchResults([]);
      setShowDropdown(false);
      fetchClientes();
    } catch (err) {
      alert('Error al vincular cliente');
    } finally {
      setVinculando(null);
    }
  };

  const handleDesvincular = async (clientId) => {
    if (!window.confirm('¿Desvincular este cliente del comercial?')) return;
    setDesvinculando(clientId);
    try {
      const res = await fetch(`${API_URL}/commercial/clientes-asociados/desvincular`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId }),
      });
      if (!res.ok) throw new Error('Error');
      fetchClientes();
    } catch (err) {
      alert('Error al desvincular cliente');
    } finally {
      setDesvinculando(null);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/comercial/comisiones')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
            <Users size={20} style={{ color: PRIMARY }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              Clientes vinculados con: <span style={{ color: PRIMARY }}>{comercialName}</span>
            </h1>
            <p className="text-sm text-gray-500">{total} clientes asociados</p>
          </div>
        </div>
      </div>

      {/* Vincular nuevo cliente */}
      <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 bg-gray-50">
        <label className="block text-sm font-medium text-gray-700 mb-1">Vincular nuevo cliente</label>
        <div ref={wrapperRef} className="relative max-w-lg">
          <div className="flex">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchInput}
                onFocus={() => { if (searchResults.length) setShowDropdown(true); }}
                placeholder="Buscar cliente por razón social..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setSearchResults([]); setShowDropdown(false); }}
                className="ml-2 px-2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {showDropdown && (searchResults.length > 0 || searching) && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {searching && <div className="px-4 py-2 text-sm text-gray-400">Buscando...</div>}
              {searchResults.map(c => (
                <div key={c.id} className="flex items-center justify-between px-4 py-2 hover:bg-blue-50 transition-colors">
                  <div>
                    <span className="text-sm font-medium text-gray-800">{c.razon_social}</span>
                    {c.cif && <span className="text-xs text-gray-400 ml-2">{c.cif}</span>}
                  </div>
                  <button
                    onClick={() => handleVincular(c.id)}
                    disabled={vinculando === c.id}
                    className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white rounded-md transition-colors hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: PRIMARY }}
                  >
                    {vinculando === c.id ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
                    Vincular
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40 gap-2 text-gray-400">
            <Loader2 size={20} className="animate-spin" />
            <span className="text-sm">Cargando...</span>
          </div>
        ) : clientes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Users size={40} />
            <span className="text-sm mt-3">No hay clientes vinculados a este comercial</span>
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-6 py-3">Cod. Cliente</th>
                <th className="px-6 py-3">Razón Social</th>
                <th className="px-6 py-3">Alias</th>
                <th className="px-6 py-3">CIF</th>
                <th className="px-6 py-3 text-center w-24">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {clientes.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">{c.id}</td>
                  <td className="px-6 py-3 text-sm text-gray-800">{c.razon_social}</td>
                  <td className="px-6 py-3 text-sm text-gray-500">{c.alias || '—'}</td>
                  <td className="px-6 py-3 text-sm text-gray-500">{c.cif || '—'}</td>
                  <td className="px-6 py-3 text-center">
                    <button
                      onClick={() => handleDesvincular(c.id)}
                      disabled={desvinculando === c.id}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                      title="Desvincular"
                    >
                      {desvinculando === c.id ? <Loader2 size={13} className="animate-spin" /> : <Unlink size={13} />}
                      Desvincular
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-white">
          <span className="text-sm text-gray-500">Página {page} de {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1.5 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1.5 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientesAsociados;
